import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import api from './api';
import { log } from './logger';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// HIPAA §164.312(a)(2)(iii) — Automatic Logoff. Match the backend
// IDLE_TIMEOUT_MINUTES (30 min). Warn 5 min before forced logout.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_WARN_MS    = 25 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
    } catch (err) {
      // 401/403 means "not logged in" — expected, not an error worth logging.
      if (err?.response?.status && ![401, 403].includes(err.response.status)) {
        log.error('Auth check failed:', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async ({ reason } = {}) => {
    try { await api.post('/auth/logout'); } catch (err) { log.error('Logout request failed:', err); }
    setUser(null);
    // Preserve the reason so /login can show a helpful banner.
    const q = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    window.location.href = `/login${q}`;
  }, []);

  // ── Automatic logoff — HIPAA safeguard ──────────────────────────
  const timeoutRef = useRef(null);
  const warnRef = useRef(null);
  useEffect(() => {
    if (!user) return; // Only arm when authenticated.
    const arm = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
      warnRef.current = setTimeout(() => {
        toast.warning(
          "You'll be signed out in 5 minutes due to inactivity. Move the mouse to stay signed in.",
          { id: 'idle-warn', duration: 10000 }
        );
      }, IDLE_WARN_MS);
      timeoutRef.current = setTimeout(() => {
        logout({ reason: 'idle_timeout' });
      }, IDLE_TIMEOUT_MS);
    };
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'focus'];
    activityEvents.forEach(ev => window.addEventListener(ev, arm, { passive: true }));
    arm();
    return () => {
      activityEvents.forEach(ev => window.removeEventListener(ev, arm));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
